import React, { useMemo, useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { resolveSchedulerTerminal } from './use-scheduler-read-model.js';
import type { SchedulerDriver, AssignmentGroups, TerminalOption } from './use-scheduler-read-model.js';

interface TerminalGroupEntry {
  terminal: TerminalOption;
  drivers: SchedulerDriver[];
}

interface DriverChipProps {
  driver: SchedulerDriver;
  onOpenDriver: (driver: SchedulerDriver) => void;
}

function DriverChip({ driver, onOpenDriver }: DriverChipProps) {
  const assignment = driver.current_assignment || null;
  const resolvedTerminal = resolveSchedulerTerminal(driver);
  const statusLabel = assignment?.assignment_type
    || (resolvedTerminal?.source === 'mirror' ? 'needs assignment' : 'unassigned');

  return (
    <button
      type="button"
      onClick={() => onOpenDriver(driver)}
      style={{ padding: '8px 10px', borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'grid', gap: 4, textAlign: 'left', cursor: 'pointer', color: C.text }}
    >
      <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{driver.first_name} {driver.last_name}</div>
      <div style={{ color: C.textMuted, fontSize: 11 }}>{driver.driver_id || 'No driver ID'} · {driver.staffing_type || 'local'}</div>
      <div style={{ color: C.textMuted, fontSize: 11 }}>
        {statusLabel}
        {resolvedTerminal?.source === 'mirror' && resolvedTerminal.name ? ` · ${resolvedTerminal.name}` : ''}
      </div>
    </button>
  );
}

interface GroupBlockProps {
  groupKey: string;
  title: string;
  subtitle: string;
  drivers: SchedulerDriver[];
  openGroups: Record<string, boolean>;
  onToggleGroup: (groupKey: string) => void;
  onOpenDriver: (driver: SchedulerDriver) => void;
  actionLabel?: string;
  onAction?: () => void;
}

function GroupBlock({ groupKey, title, subtitle, drivers, openGroups, onToggleGroup, onOpenDriver, actionLabel, onAction }: GroupBlockProps) {
  const isOpen = openGroups[groupKey] !== false;

  return (
    <div style={LAYOUT.subPanel}>
      <button
        type="button"
        onClick={() => onToggleGroup(groupKey)}
        style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', marginBottom: isOpen ? 10 : 0, flexWrap: 'wrap', width: '100%', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
      >
        <div style={{ color: C.gold, fontSize: 14 }}>{isOpen ? '▾' : '▸'} {title}</div>
        <div style={{ color: C.textMuted, fontSize: 11 }}>{subtitle}</div>
      </button>
      {onAction ? (
        <button
          type="button"
          onClick={onAction}
          style={{ marginBottom: 10, padding: '6px 10px', borderRadius: 8, border: `1px solid ${C.borderSoft}`, background: 'transparent', color: C.gold, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
        >
          {actionLabel || 'Assign here'}
        </button>
      ) : null}
      {isOpen ? (
        drivers.length === 0 ? (
          <div style={{ color: C.textMuted, fontSize: 12 }}>No drivers in this group.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            {drivers.map((driver: SchedulerDriver) => <DriverChip key={driver.id} driver={driver} onOpenDriver={onOpenDriver} />)}
          </div>
        )
      ) : null}
    </div>
  );
}

interface SchedulerAssignmentBoardProps {
  assignmentGroups: AssignmentGroups;
  search: string;
  onOpenDriver: (driver: SchedulerDriver) => void;
  onAssignTerminal?: (terminalId: string) => void;
}

export default function SchedulerAssignmentBoard({ assignmentGroups, search, onOpenDriver, onAssignTerminal }: SchedulerAssignmentBoardProps) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const filteredGroups = useMemo(() => {
    const query = String(search || '').trim().toLowerCase();
    if (!query) return assignmentGroups;

    const filterDrivers = (drivers: SchedulerDriver[]) => drivers.filter((driver: SchedulerDriver) => {
      const name = `${driver.first_name || ''} ${driver.last_name || ''}`.toLowerCase();
      const driverId = String(driver.driver_id || '').toLowerCase();
      return name.includes(query) || driverId.includes(query);
    });

    const byTerminal: Record<string, TerminalGroupEntry> = Object.fromEntries(
      Object.entries(assignmentGroups.byTerminal)
        .map(([name, group]): [string, TerminalGroupEntry] => [name, { ...group, drivers: filterDrivers(group.drivers) }])
        .filter(([, group]) => (group as TerminalGroupEntry).drivers.length),
    );

    return {
      home: filterDrivers(assignmentGroups.home),
      unassigned: filterDrivers(assignmentGroups.unassigned),
      offRotation: filterDrivers(assignmentGroups.offRotation),
      byTerminal,
    };
  }, [assignmentGroups, search]);

  const terminalGroups = Object.entries(filteredGroups.byTerminal).sort((a, b) => {
    return String(a[1].terminal.name || '').localeCompare(String(b[1].terminal.name || ''));
  });

  function toggleGroup(groupKey: string) {
    setOpenGroups((current) => ({ ...current, [groupKey]: current[groupKey] === false ? true : false }));
  }

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 12 }}>
        Assignment read surface
      </div>

      <GroupBlock groupKey="home" title="Home" subtitle={`${filteredGroups.home.length} drivers`} drivers={filteredGroups.home} openGroups={openGroups} onToggleGroup={toggleGroup} onOpenDriver={onOpenDriver} />
      <GroupBlock groupKey="unassigned" title="Unassigned" subtitle={`${filteredGroups.unassigned.length} drivers`} drivers={filteredGroups.unassigned} openGroups={openGroups} onToggleGroup={toggleGroup} onOpenDriver={onOpenDriver} />
      <GroupBlock groupKey="offRotation" title="Off rotation" subtitle={`${filteredGroups.offRotation.length} drivers`} drivers={filteredGroups.offRotation} openGroups={openGroups} onToggleGroup={toggleGroup} onOpenDriver={onOpenDriver} />

      {terminalGroups.map(([terminalId, group]: [string, TerminalGroupEntry]) => (
        <GroupBlock
          key={terminalId}
          groupKey={`terminal:${terminalId}`}
          title={group.terminal.name}
          subtitle={`${group.drivers.length} drivers`}
          drivers={group.drivers}
          openGroups={openGroups}
          onToggleGroup={toggleGroup}
          onOpenDriver={onOpenDriver}
          actionLabel="Assign drivers"
          onAction={onAssignTerminal ? () => onAssignTerminal(terminalId) : undefined}
        />
      ))}
    </div>
  );
}
